import React, { useEffect, useState } from 'react';
import {
    deleteMessageForUser,
    isMessageUnread,
    markMessageAsRead,
    messaging,
    showMessaging,
} from '../../../services/admin/Messaging.tsx';
import { Messaging } from '../../../models/Messaging.tsx';
import { TbMessages } from "react-icons/tb";
import { Timestamp } from 'firebase/firestore';
import { useUser } from '../../../context/UserContext.tsx';
import { FaSyncAlt } from 'react-icons/fa';
import '../../../styles/Messaging.css';
import {
    formatAdminFiguresDateTime,
    formatAdminFiguresTime,
    isSameAdminFiguresCalendarDay,
} from '../../../helpers/adminFiguresTime.tsx';

const formatMessageDate = (seconds: number) => {
    const date = new Date(seconds * 1000);
    if (isSameAdminFiguresCalendarDay(date, new Date())) {
        return formatAdminFiguresTime(date);
    }
    return formatAdminFiguresDateTime(date, {
        month: 'short',
        day: 'numeric',
        year: undefined,
        hour: '2-digit',
        minute: '2-digit',
    });
};

const InboxMessaging = () => {

    const { user: currentUser } = useUser();
    const [messages, setMessages] = useState<Messaging[]>([]);
    const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
    const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
    const [replyText, setReplyText] = useState<Record<string, string>>({});
    const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchMessages = async () => {
        if (!currentUser?.userName) return;
        setIsLoading(true);
        try {
            const fetchedMessages = await showMessaging(currentUser.userName);
            setMessages(fetchedMessages);
        } catch (error) {
            console.error("Error fetching messages: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
    }, [currentUser?.userName]);

    const toggleMessage = async (messageId: string) => {
        if (!currentUser?.userName) return;

        const isExpanding = !expandedMessages[messageId];
        setExpandedMessages(prev => ({
            ...prev,
            [messageId]: isExpanding,
        }));
        setReplyingToMessageId(null);

        if (isExpanding) {
            const message = messages.find(m => m.id === messageId);
            if (message && isMessageUnread(message, currentUser.userName)) {
                try {
                    await markMessageAsRead(messageId, currentUser.userName);
                    setMessages(prev => prev.map(m =>
                        m.id === messageId
                            ? { ...m, readBy: [...(m.readBy || []), currentUser.userName] }
                            : m
                    ));
                } catch (error) {
                    console.error("Error marking message as read:", error);
                }
            }
        }
    };

    const handleCheckboxChange = (messageId: string) => {
        setSelectedMessages(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(messageId)) {
                newSelected.delete(messageId);
            } else {
                newSelected.add(messageId);
            }
            return newSelected;
        });
    };

    const handleDeleteSelected = async () => {
        if (!currentUser?.userName || selectedMessages.size === 0) return;
        try {
            const deletePromises = Array.from(selectedMessages).map(messageId =>
                deleteMessageForUser(messageId, currentUser.userName)
            );
            await Promise.all(deletePromises);
            setMessages(prevMessages => prevMessages.filter(message => !selectedMessages.has(message.id || '')));
            setSelectedMessages(new Set());
        } catch (error) {
            console.error("Error deleting selected messages: ", error);
        }
    };

    const handleReplyChange = (e: React.ChangeEvent<HTMLTextAreaElement>, messageId: string) => {
        setReplyText({
            ...replyText,
            [messageId]: e.target.value,
        });
    };

    const replyToMessage = async (originalMessageId: string, originalSenderUser: string, originalSubject: string) => {
        if (!currentUser?.userName) return;
        const replyBody = replyText[originalMessageId];

        if (!replyBody?.trim()) {
            setErrorMessage('Please type something to send your reply.');
            return;
        }

        const replyMessage = {
            subject: originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`,
            messageBody: replyBody,
            senderUser: currentUser.userName,
            recipientUsers: [originalSenderUser],
            createDate: Timestamp.now(),
            replyToMessageId: originalMessageId
        };

        try {
            await messaging(replyMessage);
            setReplyText(prev => ({ ...prev, [originalMessageId]: '' }));
            setShowSuccessMessage(true);
            setTimeout(() => setShowSuccessMessage(false), 3000);
            setErrorMessage('');
            setReplyingToMessageId(null);
        } catch (error) {
            setErrorMessage('Error sending reply. Please try again.');
        }
    };

    if (!currentUser) return <div>Loading...</div>;

    if (isLoading) {
        return (
            <div className="loader-container">
                <FaSyncAlt className="loader-icon-proyect" />
            </div>
        );
    }

    const sortedMessages = messages
        .filter(message => !message.deletedBy?.includes(currentUser.userName))
        .sort((a, b) => (b.createDate.seconds - a.createDate.seconds));

    return (
        <div className='mt-2 container-messag'>
            <div className="inbox-header">
                <span>MY INBOX</span>
                <button
                    className='btn btn-outline-danger btn-sm'
                    onClick={handleDeleteSelected}
                    disabled={selectedMessages.size === 0}
                >
                    Delete Selected
                </button>
            </div>
            <div className="line-block-messag"></div>
            <div className="message-area">
                {sortedMessages.length === 0 ? (
                    <div className="messaging-empty-state">
                        <TbMessages />
                        <p>No messages in your inbox</p>
                    </div>
                ) : (
                    sortedMessages.map((message) => {
                        const unread = isMessageUnread(message, currentUser.userName);
                        const senderInitial = (message.senderUser || '?')[0].toUpperCase();

                        return (
                            <div
                                key={message.id}
                                className={`message-item ${unread ? 'message-item--unread' : ''}`}
                            >
                                <div
                                    className="message-header"
                                    onClick={() => toggleMessage(message.id || '')}
                                >
                                    <div className="message-header-info">
                                        <input
                                            id={`checkbox-${message.id}`}
                                            type="checkbox"
                                            checked={selectedMessages.has(message.id || '')}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => handleCheckboxChange(message.id || '')}
                                        />
                                        <div className="message-avatar">{senderInitial}</div>
                                        <div className="message-sender-block">
                                            <div className="message-subject">{message.subject}</div>
                                            <div className="message-sender">From: {message.senderUser}</div>
                                        </div>
                                        {unread && <span className="unread-dot" />}
                                    </div>
                                    <span className='message-date'>
                                        {formatMessageDate(message.createDate.seconds)}
                                    </span>
                                </div>
                                {expandedMessages[message.id || ''] && (
                                    <div className="message-content">
                                        <div className="line-block-messag2"></div>
                                        <span className='my-inbox-content'>{message.messageBody}</span>
                                        <div className="messaging-reply-actions">
                                            {replyingToMessageId === message.id ? (
                                                <>
                                                    <textarea
                                                        value={replyText[message.id || ''] || ''}
                                                        onChange={(e) => handleReplyChange(e, message.id || '')}
                                                        className="container-text-inbox"
                                                        placeholder="Type your reply..."
                                                    />
                                                    <div className="messaging-reply-actions">
                                                        <button
                                                            onClick={() => replyToMessage(message.id || '', message.senderUser, message.subject)}
                                                            className="btn btn-primary btn-sm button-messagin"
                                                        >
                                                            Send
                                                        </button>
                                                        <button
                                                            onClick={() => setReplyingToMessageId(null)}
                                                            className="btn btn-outline-secondary btn-sm button-messagin"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={() => setReplyingToMessageId(message.id || '')}
                                                    className="btn btn-outline-primary btn-sm button-messagin"
                                                >
                                                    Reply
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
            {showSuccessMessage && (
                <div className="success-message">Reply sent successfully.</div>
            )}
            {errorMessage && (
                <div className="error-message">{errorMessage}</div>
            )}
        </div>
    );
};

export default InboxMessaging;
